'use client';

import { getRoadmapsTypeStatuses, getRoadmapsTypeStatusesByRoadmapId } from '@/domain/roadmap/api';
import { useUserRoadmapTypes } from '@/domain/roadmap/hooks';
import { ItemTrackingStatus, type PlatformVersionDTO, type RoadMapUpdate } from '@/domain/roadmap/types';
import { type BasicRoadmapType, type RoadMapType } from '@/domain/roadmapType/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { confirmDialog } from 'primereact/confirmdialog';
import { useCallback, useMemo } from 'react';
import { type Control, Controller, type UseFormSetValue } from 'react-hook-form';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

interface TypeFieldProps {
  viewOnly: boolean;
  control: Control<RoadMapUpdate, unknown>;
  updateStatusesByVersion: (typeId: number, version: PlatformVersionDTO) => Promise<void>;
  setValue: UseFormSetValue<RoadMapUpdate>;
  withConfirmDialog?: boolean;
  confirmMessage?: string;
}

export function TypeField({
  viewOnly,
  control,
  updateStatusesByVersion,
  setValue,
  withConfirmDialog = false,
  confirmMessage,
}: Readonly<TypeFieldProps>) {
  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);
  const { groups } = useUserRoadmapTypes();

  const allTypes = useMemo(
    () => groups.flatMap((g) => (g.roadmapTypes ?? []).map((t) => ({ ...t, groupName: g.name }))),
    [groups],
  );

  const updateStatuses = useCallback(
    async (type: BasicRoadmapType) => {
      const roadmapStatus = editableRoadMap.idRoadmap
        ? await getRoadmapsTypeStatusesByRoadmapId(editableRoadMap.idRoadmap, type.idRoadmapType)
        : await getRoadmapsTypeStatuses(type.idRoadmapType);
      const statusesWithDefaults = roadmapStatus.map((s) => ({
        ...s,
        roadmapStatusItems: s.roadmapStatusItems.map((item) => ({
          ...item,
          status: item.status ?? ItemTrackingStatus.NotStarted,
        })),
      }));
      useRoadmapEditStore.setState({
        editableRoadMap: { ...editableRoadMap, status: null, type: { ...type, roadmapStatus: statusesWithDefaults } },
      });
    },
    [editableRoadMap],
  );

  const handleTypeChange = useCallback(
    (selected: BasicRoadmapType | null, formOnChange: (val: RoadMapType) => void, currentValue: RoadMapType | null) => {
      if (!selected) return;
      const doChange = async () => {
        formOnChange({ ...selected, roadmapStatus: currentValue?.roadmapStatus ?? null });
        setValue('customers', []);
        setValue('affectedVersions', []);
        setValue('fixVersions', []);
        await updateStatuses(selected);
        if (editableRoadMap.version != null) {
          await updateStatusesByVersion(selected.idRoadmapType, editableRoadMap.version);
        }
      };
      if (withConfirmDialog) {
        confirmDialog({
          message:
            confirmMessage ??
            'Changing the type will reset all quality assurance items and COSTS. Are you sure you want to proceed?',
          header: 'Change Roadmap Type',
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            void doChange();
          },
        });
      } else {
        void doChange();
      }
    },
    [updateStatuses, editableRoadMap.version, updateStatusesByVersion, setValue, withConfirmDialog, confirmMessage],
  );

  return (
    <Controller
      name="type"
      control={control}
      render={({ field: { value, onChange } }) => (
        <Box sx={{ py: 0.75, width: '100%' }}>
          <FieldActionsWrapper
            locked={editableRoadMap.locked}
            lockedTooltip="This item is scope locked. You can't change the type. To unlock it contact an admin!"
          >
            <Autocomplete<(typeof allTypes)[number], false, false, false>
              options={allTypes}
              getOptionLabel={(t) => t.name}
              isOptionEqualToValue={(a, b) => a.idRoadmapType === b.idRoadmapType}
              groupBy={(t) => t.groupName}
              value={allTypes.find((t) => t.idRoadmapType === value?.idRoadmapType) ?? null}
              onChange={(_, val) => handleTypeChange(val, onChange, value)}
              disabled={viewOnly || editableRoadMap.locked}
              label="Type *"
              size="small"
              fullWidth
            />
          </FieldActionsWrapper>
        </Box>
      )}
    />
  );
}
